import React, { useState, useEffect } from 'react';
import { DataTable } from '../ui/data-table';
import { Button } from '../ui/button';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../ui/modal';
import { Input } from '../ui/input';
import { topicAPI, courseAPI, lessonAPI, topicLessonAPI } from '../../services/api';

const Topics = () => {
  const [topics, setTopics] = useState([]);
  const [courses, setCourses] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [lessonsLoading, setLessonsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLessonModalOpen, setIsLessonModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingTopicId, setEditingTopicId] = useState(null);
  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [deletingTopicId, setDeletingTopicId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreatingTopicLesson, setIsCreatingTopicLesson] = useState(false);
  const [selectedLessons, setSelectedLessons] = useState([]);
  const [lessonOrderIndices, setLessonOrderIndices] = useState({});
  const [isViewTopicLessonsOpen, setIsViewTopicLessonsOpen] = useState(false);
  const [topicLessons, setTopicLessons] = useState([]);
  const [topicLessonsLoading, setTopicLessonsLoading] = useState(false);
  const [editingTopicLesson, setEditingTopicLesson] = useState(null);
  const [deletingTopicLessonId, setDeletingTopicLessonId] = useState(null);
  const [formData, setFormData] = useState({
    course_id: '',
    title: '',
    description: ''
  });

  const fetchTopics = async () => {
    try {
      setLoading(true);
      const response = await topicAPI.getAllTopics();
      setTopics(response.data || []);
    } catch (error) {
      console.error('Error fetching topics:', error);
      setTopics([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      setCoursesLoading(true);
      const response = await courseAPI.getAllCourses();
      setCourses(response.data || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setCourses([]);
    } finally {
      setCoursesLoading(false);
    }
  };
  
  const fetchLessons = async (course_id) => {
    try {
      setLessonsLoading(true);
      const response = await lessonAPI.getAllLessonsByCourseId(course_id);
      setLessons(response.data || []);
    } catch (error) {
      console.error('Error fetching lessons:', error);
      setLessons([]);
    } finally {
      setLessonsLoading(false);
    }
  };

  useEffect(() => {
    fetchTopics();
    fetchCourses();
  }, []);

  // Ensure courses are loaded when modal opens
  useEffect(() => {
    if (isModalOpen && courses.length === 0) {
      fetchCourses();
    }
  }, [isModalOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      const submitData = {
        ...formData,
        course_id: parseInt(formData.course_id)
      };
      
      if (isEditing) {
        // Update existing topic
        await topicAPI.updateTopic(editingTopicId, submitData);
      } else {
        // Create new topic
        await topicAPI.createTopic(submitData);
      }
      
      setIsModalOpen(false);
      resetForm();
      fetchTopics();
    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} topic:`, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditTopic = (topic) => {
    setIsEditing(true);
    setEditingTopicId(topic.id);
    setFormData({
      course_id: topic.course_id ? String(topic.course_id) : '',
      title: topic.title || '',
      description: topic.description || ''
    });
    setIsModalOpen(true);
  };

  const handleDeleteTopic = async (topicId) => {
    if (!window.confirm('Are you sure you want to delete this topic? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingTopicId(topicId);
      await topicAPI.deleteTopic(topicId);
      fetchTopics();
    } catch (error) {
      console.error('Error deleting topic:', error);
      alert('Failed to delete topic. Please try again.');
    } finally {
      setDeletingTopicId(null);
    }
  };

  const handleAddLessonsToTopic = (topicId, course_id) => {
    setSelectedTopicId(topicId);
    setSelectedLessons([]);
    setLessonOrderIndices({});
    setIsLessonModalOpen(true);
      fetchLessons(course_id);
  };

  const handleLessonSelection = (lessonId) => {
    setSelectedLessons(prev => {
      if (prev.includes(lessonId)) {
        // Remove lesson and its order index
        setLessonOrderIndices(prevIndices => {
          const newIndices = { ...prevIndices };
          delete newIndices[lessonId];
          return newIndices;
        });
        return prev.filter(id => id !== lessonId);
      } else {
        // Add lesson with default order index
        setLessonOrderIndices(prevIndices => ({
          ...prevIndices,
          [lessonId]: ''
        }));
        return [...prev, lessonId];
      }
    });
  };

  const handleOrderIndexChange = (lessonId, orderIndex) => {
    setLessonOrderIndices(prev => ({
      ...prev,
      [lessonId]: orderIndex
    }));
  };

  const handleCreateTopicLessons = async () => {
    if (selectedLessons.length === 0) {
      alert('Please select at least one lesson.');
      return;
    }

    // Validate that all selected lessons have order indices
    const missingOrderIndices = selectedLessons.filter(lessonId => 
      !lessonOrderIndices[lessonId] || 
      isNaN(lessonOrderIndices[lessonId]) || 
      parseInt(lessonOrderIndices[lessonId]) < 0
    );

    if (missingOrderIndices.length > 0) {
      alert('Please enter valid order indices (0 or greater) for all selected lessons.');
      return;
    }

    try {
      setIsCreatingTopicLesson(true);
      
      // Create topic lessons for each selected lesson with their individual order indices
      const promises = selectedLessons.map(lessonId => 
        topicLessonAPI.createTopicLesson({
          topic_id: selectedTopicId,
          lesson_id: lessonId,
          topic_order_index: parseInt(lessonOrderIndices[lessonId])
        })
      );
      
      await Promise.all(promises);
      topicLessonAPI.clearTopicLessonCache();
      
      setIsLessonModalOpen(false);
      setSelectedLessons([]);
      setSelectedTopicId(null);
      setLessonOrderIndices({});
      
      alert(`Successfully added ${selectedLessons.length} lesson(s) to the topic.`);
    } catch (error) {
      console.error('Error creating topic lessons:', error);
      alert('Failed to add lessons to topic. Please try again.');
    } finally {
      setIsCreatingTopicLesson(false);
    }
  };

  const handleCloseLessonModal = () => {
    setIsLessonModalOpen(false);
    setSelectedLessons([]);
    setSelectedTopicId(null);
    setLessonOrderIndices({});
  };

  const handleViewTopicLessons = async (topicId) => {
    setSelectedTopicId(topicId);
    setIsViewTopicLessonsOpen(true);
    try {
      setTopicLessonsLoading(true);
      const response = await topicLessonAPI.getTopicLessonsByTopicId(topicId);
      setTopicLessons(response.data || []);
    } catch (error) {
      console.error('Error fetching topic lessons:', error);
      setTopicLessons([]);
    } finally {
      setTopicLessonsLoading(false);
    }
  };
  
  const handleEditTopicLesson = (topicLesson) => {
    setEditingTopicLesson({
      ...topicLesson,
      topic_order_index: topicLesson.topic_order_index || ''
    });
  };

  const handleUpdateTopicLesson = async () => {
    if (!editingTopicLesson.topic_order_index || isNaN(editingTopicLesson.topic_order_index) || parseInt(editingTopicLesson.topic_order_index) < 0) {
      alert('Please enter a valid order index (0 or greater).');
      return;
    }

    try {
      await topicLessonAPI.updateTopicLesson(editingTopicLesson.id, {
        topic_id: editingTopicLesson.topic_id,
        lesson_id: editingTopicLesson.lesson_id,
        topic_order_index: parseInt(editingTopicLesson.topic_order_index)
      });
      
      topicLessonAPI.clearTopicLessonCache();
      setEditingTopicLesson(null);
      
      // Refresh topic lessons list
      handleViewTopicLessons(selectedTopicId);
      
      alert('Topic lesson updated successfully!');
    } catch (error) {
      console.error('Error updating topic lesson:', error);
      alert('Failed to update topic lesson. Please try again.');
    }
  };

  const handleDeleteTopicLesson = async (topicLessonId) => {
    console.log("delete topic lesson", topicLessonId);
    console.log("topicLessonAPI methods:", Object.keys(topicLessonAPI));
    if (!window.confirm('Are you sure you want to remove this lesson from the topic?')) {
      return;
    }

    try {
      setDeletingTopicLessonId(topicLessonId);
      await topicLessonAPI.deleteTopicLesson(topicLessonId);
      topicLessonAPI.clearTopicLessonCache();
      
      // Refresh topic lessons list
      handleViewTopicLessons(selectedTopicId);
      
      alert('Lesson removed from topic successfully!');
    } catch (error) {
      console.error('Error deleting topic lesson:', error);
      alert('Failed to remove lesson from topic. Please try again.');
    } finally {
      setDeletingTopicLessonId(null);
    }
  };

  const handleCloseViewTopicLessons = () => {
    setIsViewTopicLessonsOpen(false);
    setTopicLessons([]);
    setSelectedTopicId(null);
    setEditingTopicLesson(null);
  };

  const resetForm = () => {
    setFormData({
      course_id: '',
      title: '',
      description: ''
    });
    setIsEditing(false);
    setEditingTopicId(null);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const getCourseTitle = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return course ? course.title : 'Unknown Course';
  };

  const columns = [
    {
      accessorKey: 'id',
      header: 'ID',
      sortable: true
    },
    {
      accessorKey: 'title',
      header: 'Title',
      sortable: true
    },
    {
      accessorKey: 'description',
      header: 'Description',
      sortable: false
    },
    {
      accessorKey: 'course_title',
      header: 'Course',
      sortable: true,
      cell: (topic) => getCourseTitle(topic.course_id)
    },
    {
      accessorKey: 'created_at',
      header: 'Created At',
      sortable: true,
      cell: (row) => row.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A'
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      sortable: false,
      className: 'w-32',
      cell: (row) => (
        <div className="flex space-x-1">
          <button
            onClick={() => handleViewTopicLessons(row.id)}
            className="p-1.5 text-purple-500 hover:text-purple-700 hover:bg-purple-50 rounded-full transition-colors"
            title="View topic lessons"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
          <button
            onClick={() => handleAddLessonsToTopic(row.id, row.course_id)}
            className="p-1.5 text-green-500 hover:text-green-700 hover:bg-green-50 rounded-full transition-colors"
            title="Add lessons to topic"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </button>
          <button
            onClick={() => handleEditTopic(row)}
            className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
            title="Edit topic"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => handleDeleteTopic(row.id)}
            disabled={deletingTopicId === row.id}
            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Delete topic"
          >
            {deletingTopicId === row.id ? (
              <div className="animate-spin w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full"></div>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            )}
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="max-w-6xl mx-auto p-9">
      <div className="mb-8 border-b border-gray-200 pb-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Topics</h1>
            <p className="text-lg text-gray-600">Manage course topics</p>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            + Add Topic
          </button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={topics}
        searchKey="title"
        searchPlaceholder="Search topics..."
        loading={loading}
        emptyMessage="No topics found."
      />

      <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
        <form onSubmit={handleSubmit}>
          <ModalHeader>
            <h2 className="text-xl font-semibold">
              {isEditing ? 'Edit Topic' : 'Add New Topic'}
            </h2>
          </ModalHeader>

          <ModalBody className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>
              <Input
                id="title"
                name="title"
                type="text"
                value={formData.title}
                onChange={handleInputChange}
                required
                placeholder="Enter topic title"
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Enter topic description"
              />
            </div>

            <div>
              <label htmlFor="course_id" className="block text-sm font-medium text-gray-700 mb-1">
                Course *
              </label>
              <select
                id="course_id"
                name="course_id"
                value={formData.course_id}
                onChange={handleInputChange}
                required
                disabled={coursesLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
              >
                <option value="">
                  {coursesLoading ? 'Loading courses...' : 'Select a course'}
                </option>
                {!coursesLoading && courses.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.title || `Course ${course.id}`}
                  </option>
                ))}
              </select>
            </div>
          </ModalBody>

          <ModalFooter>
            <Button
              type="button"
              onClick={handleCloseModal}
              className="mr-2 bg-gray-500 hover:bg-gray-600"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting 
                ? (isEditing ? 'Updating...' : 'Creating...') 
                : (isEditing ? 'Update Topic' : 'Create Topic')
              }
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Add Lessons to Topic Modal */}
      <Modal isOpen={isLessonModalOpen} onClose={handleCloseLessonModal} className="max-w-6xl">
        <ModalHeader>
          <h2 className="text-xl font-semibold">Add Lessons to Topic</h2>
        </ModalHeader>

        <ModalBody className="space-y-4">
          {lessonsLoading ? (
            <div className="text-center py-4">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading lessons...</p>
            </div>
          ) : lessons.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-gray-600">No lessons available.</p>
            </div>
          ) : (
            <div>
              <p className="text-sm text-gray-600 mb-3">
                Select lessons to add to this topic and specify their order indices:
              </p>
              <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-md">
                {lessons.map((lesson) => (
                  <div
                    key={lesson.id}
                    className="flex items-center p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                  >
                    <label className="flex items-center flex-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedLessons.includes(lesson.id)}
                        onChange={() => handleLessonSelection(lesson.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-3"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{lesson.title}</p>
                        {lesson.description && (
                          <p className="text-sm text-gray-500 mt-1">{lesson.description}</p>
                        )}
                      </div>
                    </label>
                    {selectedLessons.includes(lesson.id) && (
                      <div className="ml-3 w-20">
                        <Input
                          type="number"
                          min="0"
                          value={lessonOrderIndices[lesson.id] || ''}
                          onChange={(e) => handleOrderIndexChange(lesson.id, e.target.value)}
                          placeholder="Order"
                          className="text-sm"
                          required
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {selectedLessons.length > 0 && (
                <p className="text-sm text-blue-600 mt-2">
                  {selectedLessons.length} lesson(s) selected
                </p>
              )}
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            type="button"
            onClick={handleCloseLessonModal}
            className="mr-2 bg-gray-500 hover:bg-gray-600"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleCreateTopicLessons}
            disabled={isCreatingTopicLesson || selectedLessons.length === 0 || selectedLessons.some(id => !lessonOrderIndices[id] || isNaN(lessonOrderIndices[id]))}
            className="bg-green-600 hover:bg-green-700"
          >
            {isCreatingTopicLesson 
              ? 'Adding Lessons...' 
              : `Add ${selectedLessons.length || ''} Lesson${selectedLessons.length !== 1 ? 's' : ''}`
            }
          </Button>
        </ModalFooter>
      </Modal>

      {/* View Topic Lessons Modal */}
      <Modal isOpen={isViewTopicLessonsOpen} onClose={handleCloseViewTopicLessons} className="max-w-6xl">
        <ModalHeader>
          <h2 className="text-xl font-semibold">Topic Lessons</h2>
        </ModalHeader>

        <ModalBody className="space-y-4">
          {topicLessonsLoading ? (
            <div className="text-center py-4">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading topic lessons...</p>
            </div>
          ) : topicLessons.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-gray-600">No lessons found in this topic.</p>
            </div>
          ) : (
            <div>
              <p className="text-sm text-gray-600 mb-3">
                Lessons in this topic (you can edit order index or remove lessons):
              </p>
              <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-md">
                {topicLessons.map((topicLesson) => (
                  <div
                    key={topicLesson.id}
                    className="flex items-center p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{topicLesson.lesson_name || `Lesson ${topicLesson.lesson_id}`}</p>
                      {topicLesson.lesson_description && (
                        <p className="text-sm text-gray-500 mt-1">{topicLesson.lesson_description}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">Order: {topicLesson.topic_order_index}</p>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4">
                      {editingTopicLesson && editingTopicLesson.id === topicLesson.id ? (
                        <>
                          <Input
                            type="number"
                            min="0"
                            value={editingTopicLesson.topic_order_index}
                            onChange={(e) => setEditingTopicLesson(prev => ({
                              ...prev,
                              topic_order_index: e.target.value
                            }))}
                            placeholder="Order"
                            className="text-sm w-20"
                            required
                          />
                          <button
                            onClick={handleUpdateTopicLesson}
                            className="p-1.5 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-full transition-colors"
                            title="Save changes"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                          <button
                            onClick={() => setEditingTopicLesson(null)}
                            className="p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-full transition-colors"
                            title="Cancel edit"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEditTopicLesson(topicLesson)}
                            className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
                            title="Edit order index"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteTopicLesson(topicLesson.id)}
                            disabled={deletingTopicLessonId === topicLesson.id}
                            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Remove lesson from topic"
                          >
                            {deletingTopicLessonId === topicLesson.id ? (
                              <div className="animate-spin w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full"></div>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            )}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-sm text-blue-600 mt-2">
                {topicLessons.length} lesson(s) in this topic
              </p>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            type="button"
            onClick={handleCloseViewTopicLessons}
            className="bg-gray-500 hover:bg-gray-600"
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default Topics;
